/**
 * Field groups — the deterministic recommender and the group→destination expansion
 * (audit §1.4/§2.4, §Q.2b task 8).
 *
 * A group is what makes fan-out usable at scale: "everything financial also goes to the
 * warehouse" is one decision over a group, not forty decisions over forty fields.
 *
 * ── No new AI, and that is the point ─────────────────────────────────────────
 * Every recommendation reads signals the platform already produces — the semantic `role`
 * (Layer 4 / the deterministic role classifier), the L2 inferred `type`, and the entity's
 * L4 `archetype`. Nothing here infers anything new. The consequence a tenant can feel is
 * that grouping is PREDICTABLE: the same field lands in the same group every time, and the
 * rationale is a sentence rather than a confidence score.
 *
 * ── Evidence order ───────────────────────────────────────────────────────────
 * A role match beats a type match, because role says what a field MEANS while type only
 * says what it IS — two `number` fields can be a price and a page size, and only the role
 * separates them. An entity archetype never creates membership on its own (an invoice's
 * description is not financial); it only strengthens a match already made, which is how it
 * breaks ties between two equally-evidenced groups.
 *
 * ── Open vocabulary ──────────────────────────────────────────────────────────
 * `group` is an open string. These are the groups the platform RECOMMENDS, never the ones
 * it permits — a tenant's own label flows through every path here identically.
 */

import { FIELD_GROUPS, ROLE_GROUP, TYPE_GROUP } from './generated/field-group-rules.generated.js';
import type { FieldGroupDefinition } from './generated/field-group-rules.generated.js';
import type {
  ConnectionManifest, FieldMapping, InferredEntity, InferredField, MappingManifest,
} from './generated/model.generated.js';

export type { FieldGroupDefinition };
export { FIELD_GROUPS };

/** Where the recommendation came from — shown to the tenant, not a score. */
export type GroupBasis = 'role' | 'type';

export interface GroupRecommendation {
  readonly fieldId: string;
  readonly group: string;
  readonly basis: GroupBasis;
  /** Plain-language rationale, in the tenant's terms rather than the classifier's. */
  readonly why: string;
  /** True when the entity's archetype corroborated the match (never created it). */
  readonly archetypeCorroborated: boolean;
}

const BY_KEY = new Map(FIELD_GROUPS.map((g) => [g.key, g]));

/** The definition behind a group key, if the platform recommends that group at all. A
 *  tenant's own label returns undefined — it is valid, just not one of ours. */
export function groupDefinition(key: string): FieldGroupDefinition | undefined {
  return BY_KEY.get(key);
}

/** Human label for a group; a tenant's own label is returned as they typed it. */
export function groupLabel(key: string): string {
  return BY_KEY.get(key)?.label ?? key;
}

/**
 * The group this field would be recommended, or undefined when no signal claims it.
 *
 * `archetype` is the ENTITY's L4 archetype. It is consulted only to break a tie between a
 * role match and a type match that disagree — never to place a field on its own.
 */
export function recommendGroup(
  field: Pick<InferredField, 'id' | 'displayName' | 'role' | 'type'>,
  archetype?: string | undefined,
): GroupRecommendation | undefined {
  const byRole = field.role !== undefined ? ROLE_GROUP[field.role] : undefined;
  const byType = TYPE_GROUP[field.type];

  // Role wins outright when both agree or only the role matched: it is the stronger signal.
  // When they DISAGREE, the archetype is allowed to speak — an amount-typed field on an
  // invoice is financial even if its role is the generic `quantity`.
  let chosen: { group: string; basis: GroupBasis } | undefined;
  let corroborated = false;

  if (byRole !== undefined && byType !== undefined && byRole !== byType) {
    const roleDef = BY_KEY.get(byRole);
    const typeDef = BY_KEY.get(byType);
    const roleFits = archetype !== undefined && roleDef?.archetypes?.includes(archetype) === true;
    const typeFits = archetype !== undefined && typeDef?.archetypes?.includes(archetype) === true;
    if (typeFits && !roleFits) { chosen = { group: byType, basis: 'type' }; corroborated = true; }
    else { chosen = { group: byRole, basis: 'role' }; corroborated = roleFits; }
  } else if (byRole !== undefined) {
    chosen = { group: byRole, basis: 'role' };
    corroborated = archetype !== undefined && BY_KEY.get(byRole)?.archetypes?.includes(archetype) === true;
  } else if (byType !== undefined) {
    chosen = { group: byType, basis: 'type' };
    corroborated = archetype !== undefined && BY_KEY.get(byType)?.archetypes?.includes(archetype) === true;
  }

  if (chosen === undefined) return undefined;

  const label = groupLabel(chosen.group);
  const reason = chosen.basis === 'role'
    ? `its role is "${field.role ?? ''}"`
    : `it is a ${field.type} field`;
  const corroboration = corroborated ? `, and this is ${archetype ?? ''} data` : '';

  return {
    fieldId: field.id,
    group: chosen.group,
    basis: chosen.basis,
    why: `${field.displayName} belongs in ${label} because ${reason}${corroboration}.`,
    archetypeCorroborated: corroborated,
  };
}

/**
 * Recommendations for an entity's fields.
 *
 * Fields that ALREADY carry a group are skipped by default: a recommendation over a
 * decision a tenant already made is noise, and re-offering it invites an accidental
 * overwrite. Pass `includeGrouped` when the caller genuinely wants to compare.
 */
export function recommendGroups(
  entity: Pick<InferredEntity, 'fields' | 'archetype'>,
  opts?: { readonly includeGrouped?: boolean },
): GroupRecommendation[] {
  const includeGrouped = opts?.includeGrouped === true;
  const out: GroupRecommendation[] = [];
  for (const field of entity.fields) {
    if (!includeGrouped && field.group !== undefined) continue;
    const rec = recommendGroup(field, entity.archetype);
    if (rec !== undefined) out.push(rec);
  }
  return out;
}

/** Every group in use across a manifest — the tenant's actual vocabulary, recommended or
 *  their own, which is what a group picker should offer. */
export function groupsInUse(manifest: Pick<ConnectionManifest, 'entities'>): string[] {
  const groups = new Set<string>();
  for (const entity of manifest.entities) {
    for (const field of entity.fields) {
      if (field.group !== undefined && field.group !== '') groups.add(field.group);
    }
  }
  return [...groups].sort();
}

// ── Group-scoped fan-out ──────────────────────────────────────────────────────

/** Index of `entityKey|fieldKey` → group, keyed by BOTH id and apiName because a field
 *  mapping addresses fields by apiName while the UI holds ids. */
function groupIndex(manifest: Pick<ConnectionManifest, 'entities'>): Map<string, string> {
  const index = new Map<string, string>();
  for (const entity of manifest.entities) {
    for (const field of entity.fields) {
      if (field.group === undefined || field.group === '') continue;
      for (const e of [entity.id, entity.apiName]) {
        for (const f of [field.id, field.apiName]) index.set(`${e}|${f}`, field.group);
      }
    }
  }
  return index;
}

/**
 * Expand group-scoped destinations onto the field mappings they apply to.
 *
 * This is what makes a group decision reach the executor: the sync worker plans destinations
 * per field, so a group's set is merged into every field mapping whose SOURCE field carries
 * that group, immediately before planning. Adding a field to the group is then all it takes
 * for the new field to be routed too — no re-declaration anywhere.
 *
 * A destination declared on the FIELD ITSELF with the same id WINS. A specific override
 * always beats a group default, and silently having it the other way round would mean a
 * tenant's per-field exception could be undone by editing the group.
 *
 * Returns the SAME manifest object when nothing applies, so the common path allocates
 * nothing and an untouched mapping is byte-identical.
 */
export function withGroupDestinations(
  mapping: MappingManifest,
  sourceManifest: Pick<ConnectionManifest, 'entities'>,
): MappingManifest {
  const groupSets = mapping.groupDestinations;
  if (groupSets === undefined || groupSets.length === 0) return mapping;

  const byGroup = new Map(groupSets.map((g) => [g.group, g.destinations]));
  const groups = groupIndex(sourceManifest);
  let changed = false;

  const entityMappings = mapping.entityMappings.map((em) => {
    const fieldMappings = em.fieldMappings.map((fm): FieldMapping => {
      const group = groups.get(`${fm.sourceEntityId}|${fm.sourceFieldId}`);
      if (group === undefined) return fm;
      const groupDestinations = byGroup.get(group);
      if (groupDestinations === undefined || groupDestinations.length === 0) return fm;

      const own = fm.destinations ?? [];
      const ownIds = new Set(own.map((d) => d.id));
      const inherited = groupDestinations.filter((d) => !ownIds.has(d.id));
      if (inherited.length === 0) return fm;

      changed = true;
      return { ...fm, destinations: [...own, ...inherited] };
    });
    return changed ? { ...em, fieldMappings } : em;
  });

  return changed ? { ...mapping, entityMappings } : mapping;
}
